import React, { useState, useEffect } from 'react';
import {
  getFeaturedArticles,
  getArticlesByCategory,
  getForYouArticles,
  getTrendingArticles,
  getCategories
} from '../utils/dataService';
import { 
  getCategoryName, 
  getCategoryTextClass, 
  getCategoryHoverClass, 
  getCategoryBadgeClass 
} from '../utils/helpers'; // Import all new helpers
import FeaturedArticleCard from '../components/FeaturedArticleCard';
import ArticleCard from '../components/ArticleCard';
import Advertisement from '../components/Advertisement';
import HeroCarousel from '../components/HeroCarousel';
import GamNativeAd from '../components/GamNativeAd';
import useAdLoader from '../hooks/useAdLoader'; // Import the custom hook

// Helper to create slugs (basic example)
const slugify = (text) => {
  if (!text) return '';
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')       // Replace spaces with -
    .replace(/[^\w\-]+/g, '')    // Remove all non-word chars
    .replace(/\-\-+/g, '-')    // Replace multiple - with single -
    .replace(/^-+/, '')        // Trim - from start of text
    .replace(/-+$/, '');       // Trim - from end of text
};

const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({}); // 动态存储各类别文章
  const [categories, setCategories] = useState([]); // 存储所有类别
  const [forYouArticles, setForYouArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentForYouIndex, setCurrentForYouIndex] = useState(0);
  const [showAllTrending, setShowAllTrending] = useState(false);
  const [isShortHeight, setIsShortHeight] = useState(window.innerHeight < 750);
  
  // Newsletter subscription states
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');

  useAdLoader([loading]); // Call the useAdLoader hook, and trigger it when loading is complete

  // 监听窗口高度变化
  useEffect(() => {
    const handleResize = () => {
      setIsShortHeight(window.innerHeight < 750);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 首先获取所有类别
        const allCategories = await getCategories();
        setCategories(allCategories);
        
        // 获取基础数据
        const [featured, forYou, trending] = await Promise.all([
          getFeaturedArticles(),
          getForYouArticles(),
          getTrendingArticles(8) // 获取所有8篇trending文章
        ]);

        setFeaturedArticles(featured);
        setForYouArticles(forYou);
        setTrendingArticles(trending);
        
        // 动态获取每个类别的文章
        const categoryData = {};
        for (const category of allCategories) {
          const articles = await getArticlesByCategory(category.slug);
          categoryData[category.slug] = articles;
        }
        setCategoryArticles(categoryData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // For You carousel navigation
  const nextForYou = () => {
    setCurrentForYouIndex((prev) => (prev + 1) % Math.max(1, forYouArticles.length));
  };

  const prevForYou = () => {
    setCurrentForYouIndex((prev) => (prev - 1 + Math.max(1, forYouArticles.length)) % Math.max(1, forYouArticles.length));
  };

  // Auto-advance For You carousel
  useEffect(() => {
    if (forYouArticles.length > 1) {
      const interval = setInterval(nextForYou, 5000);
      return () => clearInterval(interval);
    }
  }, [forYouArticles.length]);

  // Handle newsletter subscription
  const handleSubscribe = (e) => {
    e.preventDefault();
    
    // Simple email validation using regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setSubscriptionStatus('error');
      setSubscriptionMessage('Please enter a valid email address.');
      return;
    }
    
    // Simulate successful subscription
    setSubscriptionStatus('success');
    setSubscriptionMessage('Thanks for subscribing! You\'ll receive our newsletter soon.');
    setEmail('');
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSubscriptionStatus(null);
      setSubscriptionMessage('');
    }, 5000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* For You and Trending Sections - Moved to top */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* For You Carousel Section */}
          <div className={`bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden flex flex-col ${isShortHeight ? 'order-2' : 'order-2 lg:order-1'}`}>
            <div className="px-3 py-2 border-b border-stone-100">
              <h2 className="text-lg font-bold text-stone-900 mb-0">For You</h2>
            </div>
            
            {forYouArticles.length > 0 ? (
              <div className="relative flex flex-col h-[300px] sm:h-[400px]">
                <a href={`/article.html?id=${forYouArticles[currentForYouIndex].id}`} className="block flex-grow group">
                  <div className="p-3 h-full flex flex-col">
                    <div className="relative flex-grow overflow-hidden rounded-lg">
                      <img 
                        src={forYouArticles[currentForYouIndex].imageUrl || forYouArticles[currentForYouIndex].image || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80'}
                        alt={forYouArticles[currentForYouIndex].title}
                        className="w-full h-68 object-cover rounded-lg transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-lg transition-all duration-300 group-hover:from-black/80 group-hover:via-black/30"></div>
                      <div className="absolute top-3 left-3 transform transition-transform duration-300 group-hover:scale-105">
                        <span className={`inline-block px-2 py-1 text-xs font-medium border rounded-md shadow-sm transition-all duration-300 group-hover:bg-white group-hover:shadow-lg ${getCategoryBadgeClass(getCategoryName(forYouArticles[currentForYouIndex].category))}`}>
                          {getCategoryName(forYouArticles[currentForYouIndex].category)}
                        </span>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 md:inset-x-3 md:bottom-3 md:top-[55%] transform transition-transform duration-300 group-hover:translate-y-[-2px]">
                        <h3 className="text-white font-bold text-base sm:text-lg mb-1 sm:mb-2 line-clamp-2 transition-all duration-300 group-hover:text-white group-hover:drop-shadow-lg">
                          {forYouArticles[currentForYouIndex].title}
                        </h3>
                        {forYouArticles[currentForYouIndex].excerpt && (
                          <p className="text-white/90 text-xs sm:text-sm mb-1 sm:mb-2 line-clamp-2 md:line-clamp-3 leading-relaxed transition-all duration-300 group-hover:text-white/95">
                            {forYouArticles[currentForYouIndex].excerpt}
                          </p>
                        )}
                        <div className="flex items-center text-white/90 text-xs sm:text-sm transition-all duration-300 group-hover:text-white/95">
                          <span>May 28, 2025</span>
                          <span className="mx-2">•</span>
                          <span>3 min</span>
                        </div>
                      </div>
                      
                      {/* Navigation Arrows - Fixed to image */}
                      {forYouArticles.length > 1 && (
                        <>
                          <div className="absolute top-1/2 left-2 transform -translate-y-1/2 z-20">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                prevForYou();
                              }}
                              className="bg-white/80 hover:bg-white/90 rounded-full p-2 shadow-md transition-all duration-200 hover:scale-110"
                            >
                              <svg className="w-5 h-5 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="absolute top-1/2 right-2 transform -translate-y-1/2 z-20">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                nextForYou();
                              }}
                              className="bg-white/80 hover:bg-white/90 rounded-full p-2 shadow-md transition-all duration-200 hover:scale-110"
                            >
                              <svg className="w-5 h-5 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </a>
                
                {/* Dots Indicator - Fixed position outside image */}
                {forYouArticles.length > 1 && (
                  <div className="flex justify-center space-x-2 py-2">
                    {forYouArticles.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCurrentForYouIndex(index);
                        }}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentForYouIndex ? 'bg-slate-600' : 'bg-stone-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="px-3 py-2 text-center text-stone-500 flex-1 flex items-center justify-center h-[300px] sm:h-[400px]">
                <p>No articles available for recommendations</p>
              </div>
            )}
          </div>

          {/* Advertisement - Adaptive position based on screen height */}
          <div className={`lg:col-span-2 lg:mt-4 ${isShortHeight ? 'order-1' : 'order-1 lg:order-3'}`}>
            <Advertisement adId="seattle-ad-10001" />
          </div>
      
          {/* Trending Section */}
          <div className={`bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden flex flex-col ${isShortHeight ? 'order-3' : 'order-3 lg:order-2'}`}>
            <div className="px-3 py-2 border-b border-stone-100">
              <h2 className="text-lg font-bold text-stone-900 mb-0">Trending</h2>
            </div>
            
            {trendingArticles.length > 0 ? (
              <div className="p-3 flex-1 flex flex-col">
                <div className="space-y-3">
                  {trendingArticles.slice(0, showAllTrending ? trendingArticles.length : 2).map((article, index) => (
                    <ArticleCard
                      key={article.id}
                      article={article}
                      layout="horizontal"
                      showCategory={false}
                      showDescription={true}
                      priority={index < 2}
                      compact={false}
                    />
                  ))}
                </div>
                
                {trendingArticles.length > 2 && (
                  <div className="pt-3 border-stone-100 mt-auto">
                    <button 
                      onClick={() => setShowAllTrending(!showAllTrending)}
                      className="w-full px-4 py-2 text-base text-slate-600 hover:text-slate-700 hover:bg-stone-50 rounded-md transition-colors font-medium"
                    >
                      {showAllTrending ? 'Show Less' : `Show More`}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-3 text-center text-stone-500 flex-1 flex items-center justify-center">
                <p>No trending articles available</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="grid gap-8">
          {categories.map((category, categoryIndex) => {
            const articles = categoryArticles[category.slug] || [];
            
            if (articles.length === 0) return null; // 如果没有文章就不显示这个类别
            
            return (
              <div key={category.id} className="space-y-5">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-2xl font-bold text-stone-900">{category.name}</h2>
                  <a 
                    href={`/category.html?category=${category.slug}`}
                    className="text-slate-600 hover:text-slate-700 font-medium transition-colors hover:underline ml-4"
                  >
                    View All →
                  </a>
                </div>
                
                {/* Featured Article */}
                {articles[0] && (
                  <div className="mb-5">
                    <FeaturedArticleCard
                      article={articles[0]}
                      priority={categoryIndex === 0}
                    />
                  </div>
                )}
                
                {/* Regular Articles Grid */}
                {articles.length > 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {articles.slice(1, 9).map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        showCategory={true}
                      />
                    ))}
                  </div>
                )}

                {/* Ad after Cultural Experiences (Ad 10004) */}
                {category.name === 'Cultural Experiences' && (
                  <div className="!mt-8 !mb-6">
                    <Advertisement adId="seattle-ad-10004" />
                  </div>
                )}
                
                {/* Ad after Hidden Destinations (Ad 10002) */}
                {category.name === 'Hidden Destinations' && (
                  <div className="!mt-8 !mb-6">
                    <Advertisement adId="seattle-ad-10002" />
                  </div>
                )}

                {/* Ad after Adventure Escapes (Ad 10003) */}
                {category.name === 'Adventure Escapes' && (
                  <div className="!mt-8 !mb-6">
                    <Advertisement adId="seattle-ad-10003" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-br from-slate-50 to-stone-50 rounded-2xl p-8 md:p-12 border-2 border-slate-200 shadow-lg">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-stone-900">Join Our Travel Community</h2>
            <p className="text-lg text-stone-600 mb-8">
              Subscribe to TravelRadar for weekly travel insights, destination guides, and adventure inspiration from around the world.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Your email address" 
                className={`flex-1 px-4 py-3 rounded-lg border-2 ${
                  subscriptionStatus === 'error' 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-slate-300 focus:ring-slate-500'
                } focus:ring-2 focus:border-transparent outline-none bg-white shadow-sm`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button 
                type="submit" 
                className="bg-slate-600 hover:bg-slate-700 px-8 py-3 rounded-lg font-semibold transition-colors text-white shadow-md hover:shadow-lg"
              >
                Subscribe
              </button>
            </form>
            
            {subscriptionStatus && (
              <div 
                className={`mt-4 px-4 py-3 rounded-lg text-sm border-2 ${
                  subscriptionStatus === 'success' 
                    ? 'bg-green-50 text-green-800 border-green-300' 
                    : 'bg-red-50 text-red-800 border-red-300'
                }`}
              >
                {subscriptionMessage}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
